import { useMemo, useState } from "react"
import { Redirect } from "react-router-dom"
import styled from "styled-components"

import {
  Banner,
  breakpoint,
  colors,
  Skeleton,
  spacing,
} from "@minuthq/meatball-ui-react"
import { startOfMinute } from "date-fns"
import { fromZonedTime, toZonedTime } from "date-fns-tz"

import { mainContentPadding } from "src/components/AppLayout/sharedStyles"
import { EmptyState } from "src/components/EmptyState"
import { GuestConversation } from "src/components/GuestInbox/GuestConversations/GuestConversation"
import { GuestSource } from "src/components/Guests/GuestDetails/GuestSource"
import { extendGuest } from "src/components/Homes/Guests/guestsUtil"
import { TSettingContainerOnSaveReturnType } from "src/components/Settings/Setting/SettingContainer"
import { SettingDateTime } from "src/components/Settings/Setting/SettingDateTime"
import { SettingPhoneNumber } from "src/components/Settings/Setting/SettingPhoneNumber"
import { SettingStatic } from "src/components/Settings/Setting/SettingStatic"
import { SettingText } from "src/components/Settings/Setting/SettingText"
import { useBackendFlags } from "src/data/flags/useBackendFlags"
import { useFetchSMSRestrictedCountries } from "src/data/guestCommunication/queries/guestCommunicationQueries"
import {
  useFetchHomeGuest,
  usePutGuest,
} from "src/data/guests/queries/guestQueries"
import { TPutGuestRequestBody } from "src/data/guests/types/guestTypes"
import { useFetchIntegrations } from "src/data/integrations/queries/integrationQueries"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { useFetchHome } from "src/data/organizations/queries/homeQueries"
import { useGetUser } from "src/data/user/hooks/useGetUser"
import { langKeys } from "src/i18n/langKeys"
import { useTranslate } from "src/i18n/useTranslate"
import { Routes } from "src/router/routes"
import { Container } from "src/ui/Container/Container"
import { Editable } from "src/ui/Editable/Editable"
import Svg404 from "src/ui/icons/404.svg"
import { BreadcrumbView } from "src/ui/Layout/BreadcrumbView"
import {
  isValidCountry,
  validatePhoneNumber,
} from "src/ui/PhoneNumberInput/phoneNumberUtil"
import { ErrorService } from "src/utils/ErrorService"

export function GuestDetails({
  homeId,
  guestId,
}: {
  homeId: string
  guestId: string
}) {
  const { compass_released } = useBackendFlags()

  const { t } = useTranslate()

  const { org } = useOrganization()
  const user = useGetUser()
  const clockType = user.clock_type

  const [phoneCountryCode, setPhoneCountryCode] = useState("")

  const fetchHomeGuest = useFetchHomeGuest({ homeId, guestId })
  const fetchHome = useFetchHome({ orgId: org.id, homeId })
  const fetchSMSRestrictedCountries = useFetchSMSRestrictedCountries({})

  const fetchIntegrations = useFetchIntegrations({
    orgId: org.id,
    filters: { includeExternalIntegrations: true },
  })
  const putGuest = usePutGuest()

  const extendedGuest = useMemo(() => {
    const guest = fetchHomeGuest.data

    if (!guest) {
      return
    }

    const integrations = fetchIntegrations.data?.integrations ?? []

    return extendGuest(guest, integrations)
  }, [fetchHomeGuest.data, fetchIntegrations.data?.integrations])

  if (!compass_released) {
    return <Redirect to={Routes.Dashboard.location()} />
  }

  async function updatePhoneNumber(
    newPhone: string,
    countryCode: string
  ): TSettingContainerOnSaveReturnType {
    if (!validatePhoneNumber(newPhone)) {
      return {
        isSuccess: false,
        message: t(langKeys.add_stay_dialog_phone_number_error),
      }
    }

    setPhoneCountryCode(countryCode)

    return await updateGuest({ phone_number: newPhone })
  }

  async function updateGuest(
    updatedGuest: Partial<TPutGuestRequestBody>
  ): TSettingContainerOnSaveReturnType {
    try {
      const guest = fetchHomeGuest.data

      if (!guest) {
        ErrorService.captureException("Guest not found when updating guest")

        return {
          isSuccess: false,
          message: t(langKeys.failed_general_error_body),
        }
      }

      const checkin_time = parseCheckInOutTime(
        updatedGuest.checkin_time || guest.checkin_time || "",
        homeTimezone
      )

      const checkout_time = parseCheckInOutTime(
        updatedGuest.checkout_time || guest.checkout_time || "",
        homeTimezone
      )

      const newGuest: TPutGuestRequestBody = {
        ...guest,
        ...updatedGuest,
        // The following properties are required by PUT but not from GET, thus this workaround
        checkin_time,
        checkout_time,
      }

      await putGuest.mutateAsync({
        path: { home_id: homeId, guest_id: guestId },
        body: newGuest,
      })

      return {
        isSuccess: true,
      }
    } catch (error) {
      ErrorService.captureException(error)

      return {
        isSuccess: false,
      }
    }
  }

  if (
    fetchHomeGuest.isLoading ||
    fetchHome.isLoading ||
    fetchSMSRestrictedCountries.isLoading ||
    fetchIntegrations.isLoading ||
    !extendedGuest
  ) {
    return (
      <ViewWrapper>
        <Skeleton height={50} width="30ch" />
        <Skeleton height={30} width="40ch" />
        <Skeleton height={30} width="40ch" />
        <Skeleton height={30} width="40ch" />
      </ViewWrapper>
    )
  }

  if (fetchHomeGuest.error && fetchHomeGuest.error.status === 404) {
    return (
      <EmptyState
        title={t(langKeys.failed_general_error_title)}
        body={t(langKeys.failed_general_error_body)}
        icon={<Svg404 />}
      />
    )
  }

  if (
    fetchHomeGuest.isError ||
    fetchHome.isError ||
    fetchSMSRestrictedCountries.isError
  ) {
    return (
      <ViewWrapper>
        <Banner type="error">{t(langKeys.failed_contact_support)}</Banner>
      </ViewWrapper>
    )
  }

  const restrictedCountry = fetchSMSRestrictedCountries.data?.find(
    (c) => c.iso === phoneCountryCode
  )
  const isSMSRestrictedCountry = !!restrictedCountry

  const home = fetchHome.data
  const isReadOnly = !!extendedGuest.readonly
  const isExternal = extendedGuest.source !== "minut" && isReadOnly

  const homeCountryCode = isValidCountry(home.address?.country)
    ? home.address?.country
    : undefined

  const homeTimezone = home.timezone

  const zonedCheckIn = extendedGuest.checkin_time
    ? dateToZonedTime(extendedGuest.checkin_time, homeTimezone)
    : null
  const zonedCheckOut = extendedGuest.checkout_time
    ? dateToZonedTime(extendedGuest.checkout_time, homeTimezone)
    : null

  return (
    <Container>
      <Wrapper>
        <ViewWrapper>
          <BreadcrumbView
            title={
              <Editable
                onSave={async (newName) => {
                  // Editable does not like the return type of Settings
                  await updateGuest({ name: newName })
                  return
                }}
                disabled={isReadOnly}
              >
                {extendedGuest.name}
              </Editable>
            }
            titleVariant="heading1"
            breadcrumbs={[
              {
                label: t(langKeys.guest_plural),
                to: Routes.HomeGuests.location(homeId),
              },
              {
                label: extendedGuest.name,
                to: Routes.HomeGuest.location({ homeId, guestId }),
              },
            ]}
          >
            <BannerWrapper hidden={!isSMSRestrictedCountry && !isExternal}>
              {isExternal && (
                <Banner type="info" fullWidth>
                  {t(langKeys.add_stay_dialog_visit_source, {
                    sourceName: extendedGuest.sourceName,
                  })}
                </Banner>
              )}

              {isSMSRestrictedCountry && (
                <>
                  {extendedGuest.email ? (
                    <Banner type="warning" fullWidth>
                      {t(langKeys.sms_restriction_email_backup_text, [
                        restrictedCountry.name,
                      ])}
                    </Banner>
                  ) : (
                    <Banner type="error" fullWidth>
                      {t(langKeys.sms_restriction_no_email, [
                        restrictedCountry.name,
                      ])}
                    </Banner>
                  )}
                </>
              )}
            </BannerWrapper>

            <SettingsList>
              <SettingDateTime
                title={t(langKeys.guest_check_in)}
                value={zonedCheckIn}
                onSave={async (newCheckinTime) => {
                  return await updateGuest({
                    checkin_time: newCheckinTime?.toISOString() ?? "",
                  })
                }}
                dateTimePickerProps={{
                  timePickerInputProps: {
                    clockType: clockType,
                    interval: 5,
                  },
                }}
                disabled={isReadOnly}
                clockType={clockType}
              />

              <SettingDateTime
                title={t(langKeys.guest_check_out)}
                value={zonedCheckOut}
                onSave={async (newCheckoutTime) => {
                  return await updateGuest({
                    checkout_time: newCheckoutTime?.toISOString() ?? "",
                  })
                }}
                dateTimePickerProps={{
                  timePickerInputProps: {
                    clockType: clockType,
                    interval: 5,
                  },
                  datePickerProps: {
                    dateRangeProps: {
                      minBookingDate: extendedGuest.checkin_time
                        ? new Date(extendedGuest.checkin_time)
                        : undefined,
                    },
                  },
                }}
                disabled={isReadOnly}
                clockType={clockType}
              />

              <SettingPhoneNumber
                title={t(langKeys.guest_phone_number)}
                value={extendedGuest.phone_number ?? ""}
                onSave={updatePhoneNumber}
                phoneNumberInputProps={{
                  defaultCountry: homeCountryCode,
                  helperText: t(langKeys.add_stay_dialog_phone_number_error),
                  countryCallingCodeEditable: true,
                }}
                disabled={isReadOnly}
              />

              <SettingText
                title={t(langKeys.email)}
                value={extendedGuest.email ?? ""}
                onSave={(newEmail) => {
                  return updateGuest({ email: newEmail })
                }}
                disabled={isReadOnly}
              />

              <SettingStatic
                title={t(langKeys.lock_access_code)}
                value={extendedGuest.access_code}
              />

              <SettingStatic
                title={t(langKeys.guest_source)}
                value={<GuestSource extendedGuest={extendedGuest} />}
              />
            </SettingsList>
          </BreadcrumbView>
        </ViewWrapper>
        <ConversationWrapper>
          <GuestConversation orgId={org.id} guestId={guestId} />
        </ConversationWrapper>
      </Wrapper>
    </Container>
  )
}

const Wrapper = styled.div`
  @container (${breakpoint.normalUp}) {
    display: grid;
    grid-template-columns: 2fr 1fr;
    height: 100vh;
    overflow: hidden;
  }
`

const ViewWrapper = styled.div`
  ${mainContentPadding}

  @container (${breakpoint.normalUp}) {
    height: 100vh;
    overflow: auto;
  }
`

const ConversationWrapper = styled.div`
  border: 1px solid ${colors.divider};
  height: 1000px;

  @container (${breakpoint.normalUp}) {
    height: 100vh;
  }
`

const BannerWrapper = styled.div`
  display: grid;
  gap: ${spacing.S};
  margin-bottom: ${spacing.L};
`

const SettingsList = styled.div`
  & > *:not(:last-child) {
    border-bottom: 1px solid ${colors.divider};
    margin-bottom: ${spacing.L};
    padding-bottom: ${spacing.S};
  }
`

function parseCheckInOutTime(date: string, timezone: string): string {
  if (!date) {
    return ""
  }

  return startOfMinute(
    zonedDateToUtcTime(new Date(date), timezone)
  ).toISOString()
}

function dateToZonedTime(date: string, timezone?: string) {
  return timezone ? toZonedTime(date, timezone) : new Date(date)
}

function zonedDateToUtcTime(date: Date, timezone?: string) {
  return timezone ? fromZonedTime(date, timezone) : date
}
